import type { ChatMessage, ChatMessageOptions } from "@/contexts/auth-api/chat-types"
import { chatTreatyClient } from "@/contexts/auth-api/treaty-client"

interface ChatApiDeps {
  getToken: () => string | null
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

function pickTreatyPayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value

  const direct = value as Record<string, unknown>
  if (typeof direct.success === "boolean") return direct

  for (const nested of Object.values(direct)) {
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).success === "boolean") {
      return nested
    }
  }

  return value
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const data = pickTreatyPayload(response.data)
  if (data && typeof data === "object") return data as T

  const errorValue = pickTreatyPayload(response.error?.value)
  if (errorValue && typeof errorValue === "object") {
    return errorValue as T
  }

  return { success: false, message: fallbackMessage } as T
}

export function createChatApi(deps: ChatApiDeps) {
  const getMessages = async (agencyId: string): Promise<{
    success: boolean
    messages?: ChatMessage[]
    isAgencyAdmin?: boolean
    currentUserMuted?: boolean
    mutedUserIds?: string[]
    chatSystemOnlyMode?: boolean
    chatSystemOnlyUntil?: string | null
    chatSystemOnlyReason?: string | null
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.messages.get({
        query: { agencyId },
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      const data = unwrapTreatyResult<{
        success: boolean
        messages?: ChatMessage[]
        isAgencyAdmin?: boolean
        currentUserMuted?: boolean
        mutedUserIds?: string[]
        chatSystemOnlyMode?: boolean
        chatSystemOnlyUntil?: string | null
        chatSystemOnlyReason?: string | null
        message?: string
      }>(response, "Failed to get messages")

      if (data.success) {
        return {
          success: true,
          messages: data.messages as ChatMessage[] | undefined,
          isAgencyAdmin: data.isAgencyAdmin,
          currentUserMuted: data.currentUserMuted,
          mutedUserIds: data.mutedUserIds as string[] | undefined,
          chatSystemOnlyMode: data.chatSystemOnlyMode,
          chatSystemOnlyUntil: data.chatSystemOnlyUntil,
          chatSystemOnlyReason: data.chatSystemOnlyReason,
        }
      }

      return { success: false, message: data.message || "Failed to get messages" }
    } catch (error) {
      console.error("Get messages error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const sendChatMessage = async (
    agencyId: string,
    content: string,
    options?: ChatMessageOptions
  ): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const body = {
        agencyId,
        content,
        ...(options?.fileData || {}),
        messageType: options?.messageType || "text",
        gifUrl: options?.gifUrl,
        sticker: options?.sticker,
        poll: options?.poll,
      }

      const response = await chatTreatyClient.api.chat.messages.post(body, {
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to send message"
      )

      if (data.success) {
        return { success: true, chatMessage: data.chatMessage as ChatMessage | undefined }
      }

      return { success: false, message: data.message || "Failed to send message" }
    } catch (error) {
      console.error("Send message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const reactToChatMessage = async (agencyId: string, messageId: string, emoji: string): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.react.post(
        { agencyId, messageId, emoji },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to react to message"
      )
      return { success: Boolean(data.success), chatMessage: data.chatMessage as ChatMessage | undefined, message: data.message }
    } catch (error) {
      console.error("React to message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const editChatMessage = async (agencyId: string, messageId: string, content: string): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.edit.post(
        { agencyId, messageId, content },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to edit message"
      )
      return { success: Boolean(data.success), chatMessage: data.chatMessage as ChatMessage | undefined, message: data.message }
    } catch (error) {
      console.error("Edit message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const pinChatMessage = async (agencyId: string, messageId: string, pinned: boolean): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.pin.post(
        { agencyId, messageId, pinned },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to pin message"
      )
      return { success: Boolean(data.success), chatMessage: data.chatMessage as ChatMessage | undefined, message: data.message }
    } catch (error) {
      console.error("Pin message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const deleteChatMessage = async (agencyId: string, messageId: string): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.delete.post(
        { agencyId, messageId },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to delete message"
      )
      return { success: Boolean(data.success), chatMessage: data.chatMessage as ChatMessage | undefined, message: data.message }
    } catch (error) {
      console.error("Delete message error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const muteChatUser = async (agencyId: string, targetUserId: string, muted: boolean): Promise<{ success: boolean; mutedUserIds?: string[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat.mute.post(
        { agencyId, targetUserId, muted },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; mutedUserIds?: string[]; message?: string }>(
        response,
        "Failed to mute user"
      )
      return { success: Boolean(data.success), mutedUserIds: data.mutedUserIds as string[] | undefined, message: data.message }
    } catch (error) {
      console.error("Mute user error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const voteChatPoll = async (agencyId: string, messageId: string, optionId: string): Promise<{ success: boolean; chatMessage?: ChatMessage; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat["poll-vote"].post(
        { agencyId, messageId, optionId },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; chatMessage?: ChatMessage; message?: string }>(
        response,
        "Failed to vote on poll"
      )
      return { success: Boolean(data.success), chatMessage: data.chatMessage as ChatMessage | undefined, message: data.message }
    } catch (error) {
      console.error("Vote poll error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getPresignedUrl = async (
    fileName: string,
    fileType: string
  ): Promise<{ success: boolean; uploadUrl?: string; fileUrl?: string; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await chatTreatyClient.api.chat["presigned-url"].post(
        { fileName, fileType },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{
        success: boolean
        uploadUrl?: string
        fileUrl?: string
        message?: string
      }>(response, "Failed to get presigned URL")

      if (data.success) {
        return { success: true, uploadUrl: data.uploadUrl, fileUrl: data.fileUrl }
      }

      return { success: false, message: data.message || "Failed to get presigned URL" }
    } catch (error) {
      console.error("Get presigned URL error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  return {
    getMessages,
    sendChatMessage,
    reactToChatMessage,
    editChatMessage,
    pinChatMessage,
    deleteChatMessage,
    muteChatUser,
    voteChatPoll,
    getPresignedUrl,
  }
}
